// Light/dark theme state. The init script in index.html sets the initial
// theme pre-paint (saved choice → else OS prefers-color-scheme → else dark);
// this hook mirrors + flips the `dark` class on <html> and persists a manual
// choice to localStorage. No context needed — the only consumer is the navbar
// toggle; the DOM class is the source of truth.

import { useCallback, useState } from "react";

export type Theme = "dark" | "light";

const STORAGE_KEY = "omnibridge_theme";

/** Current theme as reflected by the `dark` class the init script applied. */
function readTheme(): Theme {
  if (typeof document === "undefined") return "dark";
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle("dark", theme === "dark");
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Private-mode / storage-disabled: theme still applies for this session.
  }
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(readTheme);

  const set = useCallback((next: Theme) => {
    applyTheme(next);
    setTheme(next);
  }, []);

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next: Theme = prev === "dark" ? "light" : "dark";
      applyTheme(next);
      return next;
    });
  }, []);

  return { theme, toggle, setTheme: set };
}
